import React, { useState, useEffect } from 'react';
import * as S from './styled';

interface DateFilterProps {
  initialDate?: string;
  finalDate?: string;
  onRangeSelect?: (startDate: string, endDate: string) => void;
}

type DateInterval = 'today' | 'yesterday' | 'thisWeek' | 'thisMonth';

const parseDate = (dateStr: string): Date => {
  const [day, month, year] = dateStr.split('/').map(Number);
  return new Date(year, month - 1, day);
};

const formatDate = (date: Date): string => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const DateFilter = ({
  initialDate,
  finalDate,
  onRangeSelect,
}: DateFilterProps) => {
  const [activeButton, setActiveButton] = useState<string | null>(null);

  const isSameDay = (date1: Date, date2: Date) => {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  };

  const selectRangeBasedOnDates = () => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    if (initialDate && finalDate) {
      const startDate = parseDate(initialDate);
      const endDate = parseDate(finalDate);

      if (isSameDay(startDate, today) && isSameDay(endDate, today)) {
        setActiveButton('today');
      } else if (
        isSameDay(startDate, yesterday) &&
        isSameDay(endDate, yesterday)
      ) {
        setActiveButton('yesterday');
      } else {
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);

        if (
          isSameDay(startDate, startOfWeek) &&
          isSameDay(endDate, endOfWeek)
        ) {
          setActiveButton('thisWeek');
        } else {
          const startOfMonth = new Date(
            today.getFullYear(),
            today.getMonth(),
            1,
          );
          const endOfMonth = new Date(
            today.getFullYear(),
            today.getMonth() + 1,
            0,
          );
          if (
            isSameDay(startDate, startOfMonth) &&
            isSameDay(endDate, endOfMonth)
          ) {
            setActiveButton('thisMonth');
          } else {
            setActiveButton(null);
          }
        }
      }
    } else {
      setActiveButton(null);
    }
  };

  useEffect(() => {
    selectRangeBasedOnDates();
  }, [initialDate, finalDate]);

  const handleRangeSelect = (range: DateInterval) => {
    if (onRangeSelect && range === activeButton) {
      onRangeSelect('', '');
      return;
    }
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    let newStartDate = today;
    let newEndDate = today;

    switch (range) {
      case 'today':
        newStartDate = today;
        newEndDate = today;
        break;
      case 'yesterday':
        newStartDate = yesterday;
        newEndDate = yesterday;
        break;
      case 'thisWeek':
        newStartDate = new Date(today);
        newStartDate.setDate(today.getDate() - today.getDay());
        newEndDate = new Date(newStartDate);
        newEndDate.setDate(newStartDate.getDate() + 6);
        break;
      case 'thisMonth':
        newStartDate = new Date(today.getFullYear(), today.getMonth(), 1);
        newEndDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;
    }

    setActiveButton(range);

    if (onRangeSelect) {
      onRangeSelect(formatDate(newStartDate), formatDate(newEndDate));
    }
  };

  const onSelect =
    (range: DateInterval) =>
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault();
      handleRangeSelect(range);
    };

  return (
    <S.Container
      onSubmit={(e) => {
        e.preventDefault();
      }}
    >
      <S.Control active={activeButton === 'today'} onClick={onSelect('today')}>
        Hoje
      </S.Control>
      <S.Control
        active={activeButton === 'yesterday'}
        onClick={onSelect('yesterday')}
      >
        Ontem
      </S.Control>
      <S.Control
        active={activeButton === 'thisWeek'}
        onClick={onSelect('thisWeek')}
      >
        Esta semana
      </S.Control>
      <S.Control
        active={activeButton === 'thisMonth'}
        onClick={onSelect('thisMonth')}
      >
        Este mês
      </S.Control>
    </S.Container>
  );
};

export default DateFilter;
